/**
 * Specification Viewer Component
 *
 * Read-only view of a specification with full content rendering.
 * Part of PowerSpec V3 - Spec-Driven Development
 *
 * Features:
 * - Renders TipTap JSON content
 * - Shows metadata (template, tags, version)
 * - Shows connected repository info if any
 * - Approval status section
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Card, Badge, Button, Spinner, Alert, Tabs } from 'flowbite-react';
import {
  HiDocumentText,
  HiCheckCircle,
  HiClock,
  HiCalendar,
  HiTag,
  HiCode,
  HiLink,
  HiUser,
  HiEye,
  HiPencilAlt,
  HiRefresh,
  HiCurrencyDollar,
  HiLightBulb,
  HiViewGrid
} from 'react-icons/hi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import SpecCostsPanel from './SpecCostsPanel';
import SpecRecommendationsPanel from './SpecRecommendationsPanel';
import SpecVersionHistory from './SpecVersionHistory';
import DependencyMatrixPanel from './DependencyMatrixPanel';
import type { VersionRecord } from '../api/specifications';

export interface SpecificationDetail {
  id: string;
  business_id: string;
  brand_id: string;
  client_id: string;
  project_id: string;
  created_by: string;
  title: string;
  content: any; // TipTap JSON
  content_markdown: string;
  template_id: string;
  status: 'draft' | 'in_review' | 'approved' | 'published';
  version: number;
  parent_spec_id: string | null;
  tags: string[];
  metadata: Record<string, any>;
  connected_repo: string | null;
  repo_paths: string[] | null;
  scanned_at: string | null;
  insights: Record<string, any> | null;
  approved_at: string | null;
  approved_by: string | null;
  approval_notes: string | null;
  view_count: number;
  edit_count: number;
  created_at: string;
  updated_at: string;
  template_name: string;
  template_category: string;
  template_structure: Record<string, any> | null;
  brand_name: string;
  brand_code: string;
  versions: Array<{
    id: string;
    business_id: string;
    version: number;
    created_at: string;
  }>;
  approval_history: Array<{
    id: string;
    submitted_by: string;
    submitted_at: string;
    assigned_to: string;
    reviewed_at: string | null;
    review_status: string;
    curator_notes: string | null;
  }>;
}

interface SpecViewerProps {
  specId: string;
  onEdit?: () => void;
  onRefresh?: () => void;
}

export const SpecViewer: React.FC<SpecViewerProps> = ({
  specId,
  onEdit,
  onRefresh
}) => {
  const [spec, setSpec] = useState<SpecificationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize read-only TipTap editor
  const editor = useEditor({
    extensions: [StarterKit],
    editable: false,
    content: spec?.content || ''
  });

  const fetchSpec = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await Make_Authorized_API_Call<SpecificationDetail>(
        `/tim/specifications/${specId}`,
        'GET'
      );

      if (isSuccessResponse(response)) {
        // Extract first record from Records array
        const specData = response.data?.Records?.[0];
        if (!specData) {
          throw new Error('No specification data in response');
        }

        setSpec(specData);
      } else {
        throw new Error(response.message || 'API returned unsuccessful response');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load specification';
      console.error('[SpecViewer] Error fetching spec:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [specId]);

  useEffect(() => {
    if (specId) {
      fetchSpec();
    }
  }, [specId, fetchSpec]);

  // Update editor content when spec changes
  useEffect(() => {
    if (editor && spec?.content) {
      editor.commands.setContent(spec.content);
    }
  }, [editor, spec]);

  const handleRefresh = () => {
    fetchSpec();
    onRefresh?.();
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'gray';
      case 'in_review':
        return 'warning';
      case 'approved':
        return 'success';
      case 'published':
        return 'info';
      default:
        return 'gray';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="failure">
        <div>
          <p className="font-semibold">Error loading specification</p>
          <p className="text-sm mt-1">{error}</p>
          <Button
            size="xs"
            color="light"
            className="mt-2"
            onClick={fetchSpec}
          >
            Retry
          </Button>
        </div>
      </Alert>
    );
  }

  if (!spec) {
    return (
      <Alert color="warning">
        <p>Specification not found</p>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {spec.title}
            </h1>
            <div className="flex flex-wrap gap-2 items-center">
              <Badge color={getStatusBadgeColor(spec.status)}>
                {formatStatus(spec.status)}
              </Badge>
              <Badge color="gray">
                v{spec.version}
              </Badge>
              {spec.approved_at && (
                <Badge color="success">
                  <HiCheckCircle className="w-3 h-3 mr-1 inline" />
                  Approved
                </Badge>
              )}
              <span className="text-xs text-gray-500 font-mono">
                {spec.business_id}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" color="light" onClick={handleRefresh}>
              <HiRefresh className="w-4 h-4" />
            </Button>
            {onEdit && (
              <Button size="sm" color="blue" onClick={onEdit}>
                <HiPencilAlt className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
          <div className="flex items-start gap-2">
            <HiDocumentText className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs text-gray-500">Template</div>
              <div className="font-medium">{spec.template_name}</div>
              <div className="text-xs text-gray-500 capitalize">{spec.template_category}</div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <HiCalendar className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs text-gray-500">Created</div>
              <div className="font-medium">{formatDate(spec.created_at)}</div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <HiClock className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs text-gray-500">Last Updated</div>
              <div className="font-medium">{formatDate(spec.updated_at)}</div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <HiEye className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <div className="text-xs text-gray-500">Views / Edits</div>
              <div className="font-medium">{spec.view_count} / {spec.edit_count}</div>
            </div>
          </div>
        </div>

        {/* Tags */}
        {spec.tags && spec.tags.length > 0 && (
          <div className="pt-4 border-t border-gray-200 mt-4">
            <div className="flex items-center gap-2 mb-2">
              <HiTag className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Tags</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {spec.tags.map((tag, index) => (
                <span
                  key={index}
                  className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Tabs for Content, Repository, Approval, Versions */}
      <Tabs variant="underline">
        {/* Content Tab */}
        <Tabs.Item active title="Content" icon={HiDocumentText}>
          <Card>
            <div className="prose max-w-none">
              {editor ? (
                <EditorContent editor={editor} />
              ) : (
                <div className="text-gray-500">Loading content...</div>
              )}
            </div>
          </Card>
        </Tabs.Item>

        {/* Repository Tab */}
        {spec.connected_repo && (
          <Tabs.Item title="Repository" icon={HiLink}>
            <Card>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <HiCode className="w-5 h-5 text-gray-400" />
                    <h3 className="font-semibold">Connected Repository</h3>
                  </div>
                  <p className="text-gray-700">{spec.connected_repo}</p>
                </div>

                {spec.repo_paths && spec.repo_paths.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Repository Paths</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {spec.repo_paths.map((path, index) => (
                        <li key={index} className="text-sm text-gray-700 font-mono">
                          {path}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {spec.scanned_at && (
                  <div className="text-sm text-gray-500">
                    Last scanned: {formatDate(spec.scanned_at)}
                  </div>
                )}

                {spec.insights && Object.keys(spec.insights).length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Code Insights</h4>
                    <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto">
                      {JSON.stringify(spec.insights, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </Card>
          </Tabs.Item>
        )}

        {/* Approval Tab */}
        {(spec.approved_at || spec.approval_history.length > 0) && (
          <Tabs.Item title="Approval" icon={HiCheckCircle}>
            <Card>
              <div className="space-y-4">
                {spec.approved_at && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center gap-2 mb-2">
                      <HiCheckCircle className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-green-900">Approved</h3>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <HiCalendar className="w-4 h-4 text-green-600" />
                        <span className="text-green-900">{formatDate(spec.approved_at)}</span>
                      </div>
                      {spec.approved_by && (
                        <div className="flex items-center gap-2">
                          <HiUser className="w-4 h-4 text-green-600" />
                          <span className="text-green-900">{spec.approved_by}</span>
                        </div>
                      )}
                      {spec.approval_notes && (
                        <div className="mt-2 p-2 bg-white rounded border border-green-200">
                          <p className="text-green-900">{spec.approval_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {spec.approval_history.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">Approval History</h4>
                    <div className="space-y-3">
                      {spec.approval_history.map((approval) => (
                        <div
                          key={approval.id}
                          className="p-3 bg-gray-50 border border-gray-200 rounded"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="font-medium text-sm">{approval.review_status}</span>
                            <span className="text-xs text-gray-500">
                              {formatDate(approval.submitted_at)}
                            </span>
                          </div>
                          <div className="text-sm space-y-1">
                            <div>
                              <span className="text-gray-500">Submitted by:</span>{' '}
                              <span className="font-medium">{approval.submitted_by}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Assigned to:</span>{' '}
                              <span className="font-medium">{approval.assigned_to}</span>
                            </div>
                            {approval.reviewed_at && (
                              <div>
                                <span className="text-gray-500">Reviewed:</span>{' '}
                                <span className="font-medium">{formatDate(approval.reviewed_at)}</span>
                              </div>
                            )}
                            {approval.curator_notes && (
                              <div className="mt-2 p-2 bg-white rounded text-gray-700">
                                {approval.curator_notes}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </Tabs.Item>
        )}

        {/* Version History Tab */}
        <Tabs.Item title="Version History" icon={HiClock}>
          <SpecVersionHistory
            specId={spec.id}
            versions={(spec.versions || []).map((v) => ({
              version_id: v.id,
              version: v.version,
              change_type: 'update',
              change_summary: null,
              changed_by: spec.created_by || '',
              created_at: v.created_at
            } as VersionRecord))}
            currentVersion={spec.version}
          />
        </Tabs.Item>

        {/* Dependencies Tab - Dependency Matrix */}
        <Tabs.Item title="Dependencies" icon={HiViewGrid}>
          <DependencyMatrixPanel specId={spec.id} clientId={spec.client_id} />
        </Tabs.Item>

        {/* Costs Tab - CPO Dashboard */}
        <Tabs.Item title="Costs" icon={HiCurrencyDollar}>
          <SpecCostsPanel specificationId={spec.id} />
        </Tabs.Item>

        {/* Recommendations Tab - CPO Dashboard */}
        <Tabs.Item title="Recommendations" icon={HiLightBulb}>
          <SpecRecommendationsPanel specificationId={spec.id} />
        </Tabs.Item>
      </Tabs>
    </div>
  );
};

export default SpecViewer;
